// TEXTURE RECIPES (the owner, Sep 30: a review of every material's look — nine new textures for each, made for that
// material and named for what they look like, never after another material). A recipe is data: a ground grey and a
// stack of layers, each a noise field (strata, cells, cracks, marbling, swirls) or a scatter of drawn details (speckle,
// brushing, hammer dimples, pits, shards, frost, traces, panels, fibres, glints). bakeRecipe draws one to a canvas.
//
// The same four rules as texture-materials.js:
//   * 1024 x 1024, and it TILES: a field is periodic on its own lattice (an integer number of cells a tile), and every
//     drawn detail near an edge is drawn again across it (wrap).
//   * DETERMINISTIC: every draw is hash01 off the recipe's own seed (nameSeed of its id), never Math.random(), so a
//     material looks the same on every client, and every member of a family wears the same texture.
//   * NEUTRAL: greys only, because the material's colour multiplies the texture on the GPU. The finished canvas is
//     levelled to the recipe's mean (180) and contrast (30), so no recipe darkens or washes out its material more than
//     another.
//   * CHEAP ENOUGH TO BAKE ON LOAD: a field is computed at 256 or 512 and drawn up (smoothed, wrap-padded so the seam
//     is exact); details are paths. No recipe takes more than a few tens of milliseconds.
import { hash01, nameSeed } from './texture-helpers.js';

const SIZE = 1024;
// A canvas, on the page or in a worker (recipe-worker.js bakes off the main thread, where there is no document).
export function recipeCanvas(w, h) {
    if (typeof document !== 'undefined' && document.createElement) { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; }
    return new OffscreenCanvas(w, h);
}
const clamp01 = (v) => v < 0 ? 0 : v > 1 ? 1 : v;
const grey = (v, a = 1) => { const g = Math.max(0, Math.min(255, Math.round(v))); return a >= 1 ? `rgb(${g},${g},${g})` : `rgba(${g},${g},${g},${a})`; };
const lerp = (a, b, t) => a + (b - a) * t;

// A seeded stream: rnd() in [0, 1), and range([a, b]) / pick helpers. One per layer, so adding a layer never moves
// the draws of the layers before it.
function stream(seed) {
    let i = 0;
    const rnd = () => hash01(seed, i++);
    const range = (r, d) => r == null ? d : Array.isArray(r) ? lerp(r[0], r[1], rnd()) : r;
    return { rnd, range };
}

// ── FIELDS ──────────────────────────────────────────────────────────────────────────────────────────────────────────
// Value noise on a g x g lattice that wraps, smoothstepped: periodic in both axes over the tile.
function lattice(seed, g) {
    const v = new Float32Array(g * g);
    for (let j = 0; j < g; j++) for (let i = 0; i < g; i++) v[j * g + i] = hash01(seed + i * 7919, j * 104729 + seed);
    return (x, y) => {   // x, y in tile units [0, 1)
        const fx = x * g, fy = y * g, i0 = Math.floor(fx), j0 = Math.floor(fy), tx = fx - i0, ty = fy - j0;
        const i = ((i0 % g) + g) % g, j = ((j0 % g) + g) % g, i1 = (i + 1) % g, j1 = (j + 1) % g;
        const sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
        const a = v[j * g + i], b = v[j * g + i1], c = v[j1 * g + i], d = v[j1 * g + i1];
        return lerp(lerp(a, b, sx), lerp(c, d, sx), sy);
    };
}
function fbm(seed, f, oct, gain) {
    const ls = [];
    for (let o = 0; o < oct; o++) ls.push(lattice(seed + o * 1013, f << o));
    let norm = 0; for (let o = 0, a = 1; o < oct; o++, a *= gain) norm += a;
    return (x, y) => { let s = 0, a = 1; for (let o = 0; o < oct; o++, a *= gain) s += a * ls[o](x, y); return s / norm; };
}
// Worley cells on an n x n jittered grid that wraps: [F1, F2, id of the nearest, its offset x, y] in cell units.
function worley(seed, n, jit) {
    const px = new Float32Array(n * n), py = new Float32Array(n * n);
    for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
        px[j * n + i] = 0.5 + (hash01(seed, j * n + i) - 0.5) * jit;
        py[j * n + i] = 0.5 + (hash01(seed + 1, j * n + i) - 0.5) * jit;
    }
    const out = [0, 0, 0, 0, 0];
    return (x, y) => {
        const fx = x * n, fy = y * n, ci = Math.floor(fx), cj = Math.floor(fy);
        let d1 = 9, d2 = 9, id = 0, ox = 0, oy = 0;
        for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
            const i = ci + di, j = cj + dj, k = (((j % n) + n) % n) * n + (((i % n) + n) % n);
            const dx = i + px[k] - fx, dy = j + py[k] - fy, d = Math.sqrt(dx * dx + dy * dy);
            if (d < d1) { d2 = d1; d1 = d; id = k; ox = dx; oy = dy; } else if (d < d2) d2 = d;
        }
        out[0] = d1; out[1] = d2; out[2] = id; out[3] = ox; out[4] = oy; return out;   // ox, oy: from here to the nearest point
    };
}

// A field layer: a value in [0, 1] at each point, shaped and drawn over the canvas.
//   kind   fbm | ridged (veins, marbling) | bands (strata, grain, agate: stripes along (bx, by), warped) |
//          cells (each Worley cell its own grey) | bumps (F1: soft mounds) | edges (F2 - F1: cracks, seams) |
//          dents, domes (each cell a hammer strike or a boss, shaded as lit from the upper left) |
//          turb (|fbm - .5|: billows) | laminate (glued layers, below: n layers, tone, glueV)
//   f      lattice cells a tile (fbm kinds), n Worley cells a tile, jit their jitter (0..1)
//   oct, gain   fbm octaves and falloff;  warp, wf   a domain warp's strength (tile units) and frequency
//   bx, by integer stripe direction for bands, and freq stripes a tile
//   lo, hi grey range;  gamma, steps (terraces), inv, thr / soft (a threshold: cracks and seams as lines)
//   blend  any canvas composite (multiply, overlay, screen, soft-light, source-over…);  a  alpha;  res  256 | 512 | 1024
function fieldLayer(ctx, L, seed) {
    const res = L.res || 256, kind = L.kind || 'fbm';
    const lam = kind === 'laminate' ? laminate(seed, L) : null;
    const base = lam || kind === 'cells' || kind === 'bumps' || kind === 'edges' || kind === 'dents' || kind === 'domes' ? null : fbm(seed, L.f || 4, L.oct || 4, L.gain ?? 0.5);
    const wor = base || lam ? null : worley(seed, L.n || 8, L.jit ?? 0.9);
    const warpX = L.warp ? fbm(seed + 31, L.wf || 3, 3, 0.5) : null, warpY = L.warp ? fbm(seed + 57, L.wf || 3, 3, 0.5) : null;
    const pad = res + 2, img = new ImageData(pad, pad), d = img.data;
    const lo = L.lo ?? 60, hi = L.hi ?? 230, g = L.gamma || 1, steps = L.steps || 0;
    const bx = L.bx ?? 0, by = L.by ?? 1, freq = L.freq || 8, bandWarp = L.bandWarp ?? 1.5;
    const bandNoise = kind === 'bands' ? base : null;
    for (let j = 0; j < pad; j++) for (let i = 0; i < pad; i++) {
        let x = (i - 1 + 0.5) / res, y = (j - 1 + 0.5) / res;
        x -= Math.floor(x); y -= Math.floor(y);
        if (warpX) { x += (warpX(x, y) - 0.5) * L.warp; y += (warpY(x, y) - 0.5) * L.warp; x -= Math.floor(x); y -= Math.floor(y); }
        let v;
        if (lam) v = clamp01(lam(x, y));
        else if (kind === 'fbm') v = base(x, y);
        else if (kind === 'ridged') v = 1 - Math.abs(base(x, y) * 2 - 1);
        else if (kind === 'turb') v = Math.abs(base(x, y) * 2 - 1);
        else if (kind === 'bands') v = 0.5 + 0.5 * Math.sin(((x * bx + y * by) * freq + bandNoise(x, y) * bandWarp) * Math.PI * 2);
        else {
            const w = wor(x, y);
            if (kind === 'cells') v = hash01(seed + 3, w[2]);
            else if (kind === 'bumps') v = clamp01(1 - w[0] * (L.k || 1.4));
            else if (kind === 'dents' || kind === 'domes') {   // each cell a dent (or dome), lit from the upper left
                const sh = (w[3] + w[4]) * 0.7071 * (kind === 'dents' ? -1 : 1);
                v = clamp01(0.5 - sh * (L.k || 1.2));
            }
            else v = clamp01((w[1] - w[0]) * (L.k || 3));   // edges: 0 on the seam
        }
        if (L.thr != null) v = clamp01((v - L.thr) / (L.soft || 0.04) + 0.5);
        if (L.inv) v = 1 - v;
        if (g !== 1) v = Math.pow(clamp01(v), g);
        if (steps) v = Math.round(v * steps) / steps;
        const gv = lo + (hi - lo) * v, o = (j * pad + i) * 4;
        d[o] = d[o + 1] = d[o + 2] = gv; d[o + 3] = 255;
    }
    const c = recipeCanvas(pad, pad);
    c.getContext('2d').putImageData(img, 0, 0);
    ctx.save();
    ctx.globalCompositeOperation = L.blend || 'source-over'; ctx.globalAlpha = L.a ?? 1;
    ctx.imageSmoothingEnabled = res < SIZE; ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(c, 1, 1, res, res, 0, 0, SIZE, SIZE);   // the padding row/column is the far edge: the seam is exact
    ctx.restore();
}

// GLUED LAMINATE (Timber's look, the owner's pick 'glulam' on the Timber Looks page, Oct 21): "six thin layers glued
// into one block, each a slightly different tone, with fine glue lines: engineered timber". `n` layers stacked across
// the tile (down y), each a tone of its own (0.9 ± `tone`), a dark glue line (`glueV`) along every boundary (a layer's
// first 5% and last 3%), and a fine grain along the length (x): two soft grain lines a layer, each layer's wandering by
// its own noise, under long fibres. The page's own formula, on this file's tiling lattices, so it repeats across a wall;
// it is a shade (0..1), and lo/hi and the recipe's levelling range it like any field.
function laminate(seed, L) {
    const n = L.n || 6, tone = L.tone ?? 0.12, glueV = L.glueV ?? 0.62;
    const tones = Array.from({ length: n }, (_, i) => 0.9 + tone * (hash01(seed + 5, i) - 0.5) * 2);
    const wander = Array.from({ length: n }, (_, i) => fbm(seed + 101 * (i + 1), 3, 2, 0.5));
    const f1 = lattice(seed + 7, 3), f2 = lattice(seed + 9, 2);
    const grain = (w) => {   // 1 between grain lines, down to 0.7 on each (width 0.1 of a line's spacing)
        const f = w - Math.floor(w), t = clamp01(Math.min(f, 1 - f) / 0.1);
        return 1 - 0.3 * (1 - t * t * (3 - 2 * t));
    };
    return (x, y) => {
        const ly = y * n, i = Math.min(n - 1, Math.floor(ly)), fv = ly - i;
        const glue = fv < 0.05 || fv > 0.97 ? glueV : 1;
        const fib = 0.5 * f1(x, (y * 12) % 1) + 0.5 * f2((x * 2) % 1, (y * 24) % 1);
        return tones[i] * glue * grain(ly * 2 + 0.25 * wander[i](x, y)) * (0.93 + 0.07 * fib);
    };
}

// ── DRAWN DETAILS ───────────────────────────────────────────────────────────────────────────────────────────────────
function wrap(x, y, r, draw) {
    const rr = Math.min(r, SIZE / 2);
    if (x > rr && x < SIZE - rr && y > rr && y < SIZE - rr) { draw(x, y); return; }
    for (let ox = -SIZE; ox <= SIZE; ox += SIZE) for (let oy = -SIZE; oy <= SIZE; oy += SIZE) {
        const px = x + ox, py = y + oy;
        if (px + rr < 0 || px - rr > SIZE || py + rr < 0 || py - rr > SIZE) continue;
        draw(px, py);
    }
}
const DEG = Math.PI / 180;
function polyline(ctx, pts) { ctx.beginPath(); pts.forEach(([x, y], i) => i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)); }
function shifted(pts, dx, dy) { return pts.map(([x, y]) => [x + dx, y + dy]); }
// A walk from (x, y): n steps of `step`, turning by up to `wig` radians each, starting along `ang`.
function walk(S, x, y, ang, n, step, wig) {
    const pts = [[x, y]];
    for (let k = 0; k < n; k++) { ang += (S.rnd() - 0.5) * 2 * wig; x += Math.cos(ang) * step; y += Math.sin(ang) * step; pts.push([x, y]); }
    return pts;
}
function strokeWrapped(ctx, pts, reach) {
    const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2, cy = (Math.min(...ys) + Math.max(...ys)) / 2;
    const r = Math.max(Math.max(...xs) - cx, Math.max(...ys) - cy) + reach;
    const wx = ((cx % SIZE) + SIZE) % SIZE, wy = ((cy % SIZE) + SIZE) % SIZE;
    wrap(wx, wy, r, (px, py) => { polyline(ctx, shifted(pts, px - cx, py - cy)); ctx.stroke(); });
}

const DETAILS = {
    // Specks and grains: n dots, radius r, grey v; soft ones fade at the rim.
    speck(ctx, L, S) {
        for (let k = 0; k < L.n; k++) {
            const x = S.rnd() * SIZE, y = S.rnd() * SIZE, r = S.range(L.r, 2), v = S.range(L.v, 90), a = S.range(L.a, 0.6);
            wrap(x, y, r, (px, py) => {
                if (L.soft) { const gr = ctx.createRadialGradient(px, py, 0, px, py, r); gr.addColorStop(0, grey(v, a)); gr.addColorStop(1, grey(v, 0)); ctx.fillStyle = gr; }
                else ctx.fillStyle = grey(v, a);
                ctx.beginPath(); ctx.arc(px, py, r, 0, Math.PI * 2); ctx.fill();
            });
        }
    },
    // Straight strokes: brushing, scratches, rain streaks. ang in degrees, angJ its jitter.
    streak(ctx, L, S) {
        ctx.lineCap = 'round';
        for (let k = 0; k < L.n; k++) {
            const x = S.rnd() * SIZE, y = S.rnd() * SIZE, len = S.range(L.len, 80), ang = ((L.ang || 0) + (S.rnd() - 0.5) * 2 * (L.angJ || 0)) * DEG;
            ctx.strokeStyle = grey(S.range(L.v, 90), S.range(L.a, 0.3)); ctx.lineWidth = S.range(L.w, 1.5);
            const dx = Math.cos(ang) * len / 2, dy = Math.sin(ang) * len / 2;
            strokeWrapped(ctx, [[x - dx, y - dy], [x + dx, y + dy]], ctx.lineWidth);
        }
    },
    // Hammer dimples: shallow round dents, lit from the upper left — a highlight on the far wall, a shadow on the near
    // one, both fading out before the rim, so a field of them overlaps into one beaten surface.
    dimple(ctx, L, S) {
        for (let k = 0; k < L.n; k++) {
            const x = S.rnd() * SIZE, y = S.rnd() * SIZE, r = S.range(L.r, 30), dp = S.range(L.depth, 0.35);
            wrap(x, y, r, (px, py) => {
                let gr = ctx.createRadialGradient(px + r * 0.3, py + r * 0.3, 0, px + r * 0.3, py + r * 0.3, r * 0.75);
                gr.addColorStop(0, grey(255, dp)); gr.addColorStop(1, grey(255, 0));
                ctx.fillStyle = gr; ctx.beginPath(); ctx.arc(px, py, r, 0, Math.PI * 2); ctx.fill();
                gr = ctx.createRadialGradient(px - r * 0.3, py - r * 0.3, 0, px - r * 0.3, py - r * 0.3, r * 0.75);
                gr.addColorStop(0, grey(20, dp)); gr.addColorStop(1, grey(20, 0));
                ctx.fillStyle = gr; ctx.beginPath(); ctx.arc(px, py, r, 0, Math.PI * 2); ctx.fill();
            });
        }
    },
    // Pits and vesicles: a dark hole with a lit lower rim. ring: a raised lip round it.
    pit(ctx, L, S) {
        for (let k = 0; k < L.n; k++) {
            const x = S.rnd() * SIZE, y = S.rnd() * SIZE, r = S.range(L.r, 8), sq = S.range(L.squash, 1);
            wrap(x, y, r * 1.4, (px, py) => {
                if (L.ring) { ctx.fillStyle = grey(235, 0.5); ctx.beginPath(); ctx.ellipse(px, py, r * 1.35, r * 1.35 * sq, 0, 0, Math.PI * 2); ctx.fill(); }
                ctx.fillStyle = grey(S.range(L.v, 40), S.range(L.a, 0.85)); ctx.beginPath(); ctx.ellipse(px, py, r, r * sq, 0, 0, Math.PI * 2); ctx.fill();
                ctx.strokeStyle = grey(240, 0.45); ctx.lineWidth = Math.max(1, r * 0.18);
                ctx.beginPath(); ctx.ellipse(px, py, r * 0.92, r * 0.92 * sq, 0, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke();
            });
        }
    },
    // Concentric rings: bubbles, spun metal, growth, ripples. k rings each.
    ring(ctx, L, S) {
        for (let m = 0; m < L.n; m++) {
            const x = S.rnd() * SIZE, y = S.rnd() * SIZE, r = S.range(L.r, 40), k = L.k || 1;
            ctx.strokeStyle = grey(S.range(L.v, 240), S.range(L.a, 0.4)); ctx.lineWidth = S.range(L.w, 2);
            wrap(x, y, r, (px, py) => { for (let q = 1; q <= k; q++) { ctx.beginPath(); ctx.arc(px, py, r * q / k, 0, Math.PI * 2); ctx.stroke(); } });
            if (L.fill) { const gv = S.range(L.fill, 220); wrap(x, y, r, (px, py) => { const gr = ctx.createRadialGradient(px - r * 0.3, py - r * 0.3, 0, px, py, r); gr.addColorStop(0, grey(gv, 0.5)); gr.addColorStop(1, grey(gv, 0)); ctx.fillStyle = gr; ctx.beginPath(); ctx.arc(px, py, r, 0, Math.PI * 2); ctx.fill(); }); }
        }
    },
    // Shards and facets: flat-shaded polygons, each its own grey, edged. sides [a, b], stretch elongates them.
    shard(ctx, L, S) {
        for (let k = 0; k < L.n; k++) {
            const x = S.rnd() * SIZE, y = S.rnd() * SIZE, r = S.range(L.r, 30), sides = Math.round(S.range(L.sides, 5)), rot = S.rnd() * Math.PI * 2, st = S.range(L.stretch, 1);
            const pts = [];
            for (let q = 0; q < sides; q++) {
                const a = q / sides * Math.PI * 2 + (S.rnd() - 0.5) * 0.6, rr = r * (0.6 + S.rnd() * 0.4);
                const ux = Math.cos(a) * rr * st, uy = Math.sin(a) * rr;
                pts.push([ux * Math.cos(rot) - uy * Math.sin(rot), ux * Math.sin(rot) + uy * Math.cos(rot)]);
            }
            const v = S.range(L.v, 200), a = S.range(L.a, 0.5);
            wrap(x, y, r * st, (px, py) => {
                polyline(ctx, shifted(pts, px, py)); ctx.closePath();
                ctx.fillStyle = grey(v, a); ctx.fill();
                if (L.edge) { ctx.strokeStyle = grey(L.edgeV ?? 255, L.edge); ctx.lineWidth = L.edgeW || 1.5; ctx.stroke(); }
            });
        }
    },
    // Frost: needle stars, each arm with side needles.
    frost(ctx, L, S) {
        ctx.lineCap = 'round';
        for (let k = 0; k < L.n; k++) {
            const x = S.rnd() * SIZE, y = S.rnd() * SIZE, len = S.range(L.len, 40), arms = L.arms || 6, rot = S.rnd() * Math.PI;
            ctx.strokeStyle = grey(S.range(L.v, 250), S.range(L.a, 0.6)); ctx.lineWidth = S.range(L.w, 1.5);
            wrap(x, y, len, (px, py) => {
                for (let q = 0; q < arms; q++) {
                    const a = rot + q / arms * Math.PI * 2, ex = px + Math.cos(a) * len, ey = py + Math.sin(a) * len;
                    ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(ex, ey); ctx.stroke();
                    for (let t = 0.35; t < 0.95; t += 0.22) {
                        const bx = px + Math.cos(a) * len * t, by = py + Math.sin(a) * len * t, bl = len * 0.28 * (1 - t);
                        for (const s of [-1, 1]) { ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(bx + Math.cos(a + s * 0.8) * bl, by + Math.sin(a + s * 0.8) * bl); ctx.stroke(); }
                    }
                }
            });
        }
    },
    // Traces: right-angled runs snapped to a grid, a pad at each end (circuitry, conduits).
    trace(ctx, L, S) {
        const gs = L.grid || 32; ctx.lineCap = 'square';
        for (let k = 0; k < L.n; k++) {
            let x = Math.floor(S.rnd() * SIZE / gs) * gs, y = Math.floor(S.rnd() * SIZE / gs) * gs;
            const pts = [[x, y]], segs = 2 + Math.floor(S.rnd() * (L.segs || 4));
            let horiz = S.rnd() < 0.5;
            for (let q = 0; q < segs; q++) { const len = (1 + Math.floor(S.rnd() * (L.len || 5))) * gs * (S.rnd() < 0.5 ? -1 : 1); if (horiz) x += len; else y += len; pts.push([x, y]); horiz = !horiz; }
            ctx.strokeStyle = grey(S.range(L.v, 230), S.range(L.a, 0.7)); ctx.lineWidth = S.range(L.w, 4);
            strokeWrapped(ctx, pts, 12);
            if (L.pad) for (const [px0, py0] of [pts[0], pts[pts.length - 1]]) {
                const wx = ((px0 % SIZE) + SIZE) % SIZE, wy = ((py0 % SIZE) + SIZE) % SIZE;
                wrap(wx, wy, L.pad * 2, (px, py) => { ctx.fillStyle = grey(S.range(L.v, 230), 0.9); ctx.beginPath(); ctx.arc(px, py, L.pad, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = grey(60, 0.8); ctx.beginPath(); ctx.arc(px, py, L.pad * 0.4, 0, Math.PI * 2); ctx.fill(); });
            }
        }
    },
    // Panels: nx x ny plates (bricked when offset), seams, a bevel lit from above, rivets at the corners.
    panel(ctx, L, S) {
        const nx = L.nx || 2, ny = L.ny || 2, w = SIZE / nx, h = SIZE / ny, gap = L.gap ?? 6, bev = L.bevel ?? 8;
        for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
            const ox = (L.offset && j % 2 ? w / 2 : 0), x = i * w + ox, y = j * h, v = S.range(L.v, 200);
            for (const sx of [0, -SIZE]) {
                const X = x + sx; if (X + w < 0 || X > SIZE) continue;
                ctx.fillStyle = grey(v, L.a ?? 0.5); ctx.fillRect(X + gap / 2, y + gap / 2, w - gap, h - gap);
                if (bev) {
                    ctx.fillStyle = grey(255, 0.35); ctx.fillRect(X + gap / 2, y + gap / 2, w - gap, bev); ctx.fillRect(X + gap / 2, y + gap / 2, bev, h - gap);
                    ctx.fillStyle = grey(0, 0.3); ctx.fillRect(X + gap / 2, y + h - gap / 2 - bev, w - gap, bev); ctx.fillRect(X + w - gap / 2 - bev, y + gap / 2, bev, h - gap);
                }
                if (gap) { ctx.fillStyle = grey(L.seamV ?? 40, 0.85); ctx.fillRect(X, y, w, gap / 2); ctx.fillRect(X, y + h - gap / 2, w, gap / 2); ctx.fillRect(X, y, gap / 2, h); ctx.fillRect(X + w - gap / 2, y, gap / 2, h); }
                if (L.rivet) for (const [rx, ry] of [[X + gap + L.rivet * 2.2, y + gap + L.rivet * 2.2], [X + w - gap - L.rivet * 2.2, y + gap + L.rivet * 2.2], [X + gap + L.rivet * 2.2, y + h - gap - L.rivet * 2.2], [X + w - gap - L.rivet * 2.2, y + h - gap - L.rivet * 2.2]]) {
                    const gr = ctx.createRadialGradient(rx - L.rivet * 0.35, ry - L.rivet * 0.35, 0, rx, ry, L.rivet);
                    gr.addColorStop(0, grey(250, 0.95)); gr.addColorStop(1, grey(70, 0.9)); ctx.fillStyle = gr; ctx.beginPath(); ctx.arc(rx, ry, L.rivet, 0, Math.PI * 2); ctx.fill();
                }
            }
        }
    },
    // Veins and cracks: wandering lines, some branching.
    vein(ctx, L, S) {
        ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        const one = (x, y, ang, n, w, depth) => {
            const pts = walk(S, x, y, ang, n, S.range(L.step, 14), L.wig ?? 0.5);
            ctx.strokeStyle = grey(S.range(L.v, 50), S.range(L.a, 0.6)); ctx.lineWidth = w;
            strokeWrapped(ctx, pts, w);
            if (L.halo) { ctx.strokeStyle = grey(L.haloV ?? 240, L.halo); ctx.lineWidth = w * 3; strokeWrapped(ctx, pts, w * 3); }
            if (depth < 2 && L.branch) for (let q = 2; q < pts.length - 2; q++) if (S.rnd() < L.branch) one(pts[q][0], pts[q][1], ang + (S.rnd() < 0.5 ? -1 : 1) * (0.5 + S.rnd() * 0.6), Math.max(3, n >> 1), w * 0.6, depth + 1);
        };
        for (let k = 0; k < L.n; k++) one(S.rnd() * SIZE, S.rnd() * SIZE, (L.ang != null ? L.ang * DEG + (S.rnd() - 0.5) * 2 * (L.angJ || 0) * DEG : S.rnd() * Math.PI * 2), Math.round(S.range(L.len, 20)), S.range(L.w, 2), 0);
    },
    // Fibres: short curved strands (felt, moss, pulp, hair). ang/angJ align them.
    fibre(ctx, L, S) {
        ctx.lineCap = 'round';
        for (let k = 0; k < L.n; k++) {
            const ang = L.ang != null ? (L.ang + (S.rnd() - 0.5) * 2 * (L.angJ || 0)) * DEG : S.rnd() * Math.PI * 2;
            const pts = walk(S, S.rnd() * SIZE, S.rnd() * SIZE, ang, L.seg || 5, S.range(L.len, 30) / (L.seg || 5), L.curl ?? 0.25);
            ctx.strokeStyle = grey(S.range(L.v, 100), S.range(L.a, 0.35)); ctx.lineWidth = S.range(L.w, 1.5);
            strokeWrapped(ctx, pts, 4);
        }
    },
    // Blobs: wobbly rounded patches (lichen, spots, nodules, puddles), rim optional.
    blob(ctx, L, S) {
        for (let k = 0; k < L.n; k++) {
            const x = S.rnd() * SIZE, y = S.rnd() * SIZE, r = S.range(L.r, 30), wob = L.k ?? 0.15, lobes = 2 + Math.floor(S.rnd() * 3), ph = S.rnd() * 6.28;
            const v = S.range(L.v, 120), a = S.range(L.a, 0.4);
            wrap(x, y, r * (1 + wob * 1.5), (px, py) => {
                ctx.beginPath();
                for (let q = 0; q <= 40; q++) { const t = q / 40 * Math.PI * 2, rr = r * (1 + wob * Math.sin(t * lobes + ph) + wob * 0.3 * Math.sin(t * (lobes + 2) + ph * 1.7)); const X = px + Math.cos(t) * rr, Y = py + Math.sin(t) * rr; if (q) ctx.lineTo(X, Y); else ctx.moveTo(X, Y); }
                ctx.closePath(); ctx.fillStyle = grey(v, a); ctx.fill();
                if (L.rim) { ctx.strokeStyle = grey(L.rimV ?? 60, L.rim); ctx.lineWidth = L.rimW || 2; ctx.stroke(); }
            });
        }
    },
    // Wavy lines right across the tile: ripples, grain, flow lines, sediment. Whole cycles, so they wrap.
    wave(ctx, L, S) {
        const vert = L.vert, cyc = L.cyc || 2;
        for (let k = 0; k < L.n; k++) {
            const base = (k + (L.jit ? S.rnd() * L.jit : 0)) * SIZE / L.n, amp = S.range(L.amp, 12), ph = S.rnd() * 6.28, ph2 = S.rnd() * 6.28, c2 = L.cyc2 || cyc * 2 + 1;
            ctx.strokeStyle = grey(S.range(L.v, 90), S.range(L.a, 0.4)); ctx.lineWidth = S.range(L.w, 2);
            for (const rep of [-SIZE, 0, SIZE]) {
                ctx.beginPath();
                for (let t = 0; t <= SIZE; t += 8) {
                    const s = t / SIZE * Math.PI * 2, off = base + rep + amp * Math.sin(s * cyc + ph) + amp * 0.35 * Math.sin(s * c2 + ph2);
                    const X = vert ? off : t, Y = vert ? t : off;
                    if (t) ctx.lineTo(X, Y); else ctx.moveTo(X, Y);
                }
                ctx.stroke();
            }
        }
    },
    // Glints: tiny four-point sparkles (mica, metallic flake, frost glitter).
    glint(ctx, L, S) {
        for (let k = 0; k < L.n; k++) {
            const x = S.rnd() * SIZE, y = S.rnd() * SIZE, r = S.range(L.r, 5), a = S.range(L.a, 0.8);
            wrap(x, y, r, (px, py) => {
                const gr = ctx.createRadialGradient(px, py, 0, px, py, r * 0.6); gr.addColorStop(0, grey(255, a)); gr.addColorStop(1, grey(255, 0));
                ctx.fillStyle = gr; ctx.beginPath(); ctx.arc(px, py, r * 0.6, 0, Math.PI * 2); ctx.fill();
                ctx.strokeStyle = grey(255, a * 0.7); ctx.lineWidth = 1;
                ctx.beginPath(); ctx.moveTo(px - r, py); ctx.lineTo(px + r, py); ctx.moveTo(px, py - r); ctx.lineTo(px, py + r); ctx.stroke();
            });
        }
    },
    // A frame round the face: a block's bevelled edge (lit top/left, shaded bottom/right). w its width, a its strength.
    frame(ctx, L) {
        const w = L.w || 24, a = L.a ?? 0.35;
        for (let q = 0; q < w; q++) {
            const t = (1 - q / w) * a;
            ctx.fillStyle = grey(255, t * 0.8); ctx.fillRect(q, q, SIZE - 2 * q, 1); ctx.fillRect(q, q, 1, SIZE - 2 * q);
            ctx.fillStyle = grey(0, t); ctx.fillRect(q, SIZE - 1 - q, SIZE - 2 * q, 1); ctx.fillRect(SIZE - 1 - q, q, 1, SIZE - 2 * q);
        }
    },
};

// Level the finished canvas to a mean grey and a contrast (the standard deviation of its greys), so recipes can be judged
// side by side on their pattern, and a subtle one still reads once a dark colour multiplies it. The stretch is held to
// 0.5 to 3 times, so a busy recipe is calmed and a faint one lifted without turning its noise into its pattern.
function level(ctx, mean, contrast, floor) {
    const img = ctx.getImageData(0, 0, SIZE, SIZE), d = img.data, n = d.length / 4;
    let s = 0, s2 = 0; for (let o = 0; o < d.length; o += 4) { s += d[o]; s2 += d[o] * d[o]; }
    const m = s / n, sd = Math.sqrt(Math.max(0, s2 / n - m * m)), k = sd > 0.5 ? Math.max(0.5, Math.min(3, contrast / sd)) : 1;
    for (let o = 0; o < d.length; o += 4) { const v = Math.max(floor, Math.min(255, mean + (d[o] - m) * k)); d[o] = d[o + 1] = d[o + 2] = v; d[o + 3] = 255; }
    ctx.putImageData(img, 0, 0);
}

// recipe: { id, ground (grey), layers: [{ t: 'field' | detail name, …params }], mean (default 180), contrast (default
// 30, the greys' standard deviation), floor (default 24) }
export function bakeRecipe(recipe) {
    const c = recipeCanvas(SIZE, SIZE);
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = grey(recipe.ground ?? 190); ctx.fillRect(0, 0, SIZE, SIZE);
    const seed = nameSeed(recipe.id);
    recipe.layers.forEach((L, i) => {
        const ls = seed + (i + 1) * 0x9e37;
        ctx.save();
        if (L.t === 'field') fieldLayer(ctx, L, ls);
        else {
            const fn = DETAILS[L.t]; if (!fn) throw new Error('texture recipe ' + recipe.id + ': no layer kind ' + L.t);
            ctx.globalCompositeOperation = L.blend || 'source-over';
            fn(ctx, L, stream(ls));
        }
        ctx.restore();
    });
    if (recipe.mean !== false) level(ctx, recipe.mean || 180, recipe.contrast || 30, recipe.floor ?? 24);
    return c;
}
export const RECIPE_LAYER_KINDS = Object.freeze(['field', ...Object.keys(DETAILS)]);
